import { Injectable } from '@angular/core';

/**
 * Service for storing and retrieving device tokens.
 * Uses IndexedDB (preferred) or localStorage (fallback) for secure storage.
 */
@Injectable({
  providedIn: 'root',
})
export class DeviceTokenStorageService {
  private readonly STORAGE_KEY = 'marina_device_token';
  private readonly DB_NAME = 'marina_storage';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'device_tokens';
  private db: IDBDatabase | null = null;
  private useIndexedDB = false;

  constructor() {
    this.initStorage();
  }

  /**
   * Initialize storage (try IndexedDB first, fallback to localStorage)
   */
  private async initStorage(): Promise<void> {
    try {
      // Check if IndexedDB is available
      if ('indexedDB' in window) {
        this.db = await this.openIndexedDB();
        this.useIndexedDB = true;
        console.log('[DeviceTokenStorageService] Using IndexedDB for device token storage');
      } else {
        console.log('[DeviceTokenStorageService] IndexedDB not available, using localStorage');
        this.useIndexedDB = false;
      }
    } catch (e) {
      console.warn('[DeviceTokenStorageService] Failed to initialize IndexedDB, using localStorage:', e);
      this.useIndexedDB = false;
    }
  }

  /**
   * Open IndexedDB database
   */
  private openIndexedDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          db.createObjectStore(this.STORE_NAME);
        }
      };
    });
  }

  /**
   * Store device token
   * Always stores in both IndexedDB (if available) and localStorage (for synchronous access in interceptors)
   */
  async storeDeviceToken(token: string): Promise<void> {
    // Always store in localStorage for synchronous access (needed by interceptors)
    this.fallbackToLocalStorage(token);
    
    // Also store in IndexedDB if available (preferred storage)
    if (this.useIndexedDB && this.db) {
      try {
        const transaction = this.db.transaction([this.STORE_NAME], 'readwrite');
        const store = transaction.objectStore(this.STORE_NAME);
        await new Promise<void>((resolve, reject) => {
          const request = store.put(token, this.STORAGE_KEY);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
        console.log('[DeviceTokenStorageService] Device token stored in IndexedDB and localStorage');
      } catch (e) {
        console.warn('[DeviceTokenStorageService] Failed to store in IndexedDB, using localStorage only:', e);
      }
    }
  }

  /**
   * Get device token (async - checks IndexedDB first, falls back to localStorage)
   */
  async getDeviceToken(): Promise<string | null> {
    if (this.useIndexedDB && this.db) {
      try {
        const transaction = this.db.transaction([this.STORE_NAME], 'readonly');
        const store = transaction.objectStore(this.STORE_NAME);
        return await new Promise<string | null>((resolve, reject) => {
          const request = store.get(this.STORAGE_KEY);
          request.onsuccess = () => resolve(request.result || null);
          request.onerror = () => reject(request.error);
        });
      } catch (e) {
        console.warn('[DeviceTokenStorageService] Failed to read from IndexedDB, using localStorage:', e);
        return this.fallbackFromLocalStorage();
      }
    } else {
      return this.fallbackFromLocalStorage();
    }
  }

  /**
   * Get device token synchronously (checks localStorage only - for use in interceptors)
   * This is a quick synchronous check. For full support including IndexedDB, use getDeviceToken().
   */
  getDeviceTokenSync(): string | null {
    return this.fallbackFromLocalStorage();
  }

  /**
   * Remove device token
   */
  async removeDeviceToken(): Promise<void> {
    if (this.useIndexedDB && this.db) {
      try {
        const transaction = this.db.transaction([this.STORE_NAME], 'readwrite');
        const store = transaction.objectStore(this.STORE_NAME);
        await new Promise<void>((resolve, reject) => {
          const request = store.delete(this.STORAGE_KEY);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
        console.log('[DeviceTokenStorageService] Device token removed from IndexedDB');
      } catch (e) {
        console.warn('[DeviceTokenStorageService] Failed to remove from IndexedDB, using localStorage:', e);
        this.fallbackRemoveFromLocalStorage();
      }
    } else {
      this.fallbackRemoveFromLocalStorage();
    }
  }

  /**
   * Fallback to localStorage
   */
  private fallbackToLocalStorage(token: string): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, token);
      console.log('[DeviceTokenStorageService] Device token stored in localStorage');
    } catch (e) {
      console.error('[DeviceTokenStorageService] Failed to store in localStorage:', e);
    }
  }

  /**
   * Fallback from localStorage
   */
  private fallbackFromLocalStorage(): string | null {
    try {
      return localStorage.getItem(this.STORAGE_KEY);
    } catch (e) {
      console.error('[DeviceTokenStorageService] Failed to read from localStorage:', e);
      return null;
    }
  }

  /**
   * Fallback remove from localStorage
   */
  private fallbackRemoveFromLocalStorage(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      console.log('[DeviceTokenStorageService] Device token removed from localStorage');
    } catch (e) {
      console.error('[DeviceTokenStorageService] Failed to remove from localStorage:', e);
    }
  }
}
